// Brand-local publish times. Mirrors creative-os-backend/src/domain/Scheduling/brandTime.ts
// so the picker and the server agree on DST double-resolve.

export const MIN_LEAD_MS = 5 * 60 * 1000;

export const HH_MM = /^([01]\d|2[0-3]):[0-5]\d$/;

export function isValidTimeZone(tz: string): boolean {
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

function tzOffsetMs(timeZone: string, at: Date): number {
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  const parts = Object.fromEntries(dtf.formatToParts(at).map((p) => [p.type, p.value]));
  const asUtc = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour) % 24,
    Number(parts.minute),
    Number(parts.second),
  );
  return asUtc - at.getTime();
}

export function brandLocalToUtc(dayUtc: Date, hhmm: string, timeZone: string): Date {
  const zone = isValidTimeZone(timeZone) ? timeZone : "UTC";
  const time = HH_MM.test(hhmm) ? hhmm : "12:00";
  const dateStr = dayUtc.toISOString().slice(0, 10);
  const guess = new Date(`${dateStr}T${time}:00.000Z`);
  const once = new Date(guess.getTime() - tzOffsetMs(zone, guess));
  return new Date(guess.getTime() - tzOffsetMs(zone, once));
}

export function toBrandLocal(iso: string, timezone: string): { day: string; time: string } {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(new Date(iso));
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "";
  const hour = get("hour") === "24" ? "00" : get("hour");
  return { day: `${get("year")}-${get("month")}-${get("day")}`, time: `${hour}:${get("minute")}` };
}

export type DstWallClock = "ok" | "nonexistent" | "ambiguous";

function wallClockInZone(at: Date, timeZone: string): { date: string; time: string } {
  const local = toBrandLocal(at.toISOString(), timeZone);
  return { date: local.day, time: local.time };
}

export function dstWallClockStatus(
  dayUtc: Date,
  hhmm: string,
  timeZone: string,
): DstWallClock {
  const zone = isValidTimeZone(timeZone) ? timeZone : "UTC";
  const time = HH_MM.test(hhmm) ? hhmm : "12:00";
  const dateStr = dayUtc.toISOString().slice(0, 10);
  const resolved = brandLocalToUtc(dayUtc, time, zone);
  const back = wallClockInZone(resolved, zone);
  if (back.date !== dateStr || back.time !== time) return "nonexistent";

  const hourMs = 60 * 60 * 1000;
  for (const delta of [-hourMs, hourMs]) {
    const neighbor = new Date(resolved.getTime() + delta);
    const neighborBack = wallClockInZone(neighbor, zone);
    if (
      neighborBack.date === dateStr &&
      neighborBack.time === time &&
      neighbor.getTime() !== resolved.getTime()
    ) {
      return "ambiguous";
    }
  }
  return "ok";
}
